(() => {
  const ATTRIBUTE_NAME = 'Dati partecipanti';
  const PARTICIPANT_COUNT_ATTRIBUTE = 'Numero partecipanti inseriti';
  const PARTICIPANT_FIELDS = [
    ['corso', 'Corso'],
    ['nome', 'Nome'],
    ['cognome', 'Cognome'],
    ['data_nascita', 'Data di nascita'],
    ['luogo_nascita', 'Luogo di nascita'],
    ['codice_fiscale', 'Codice fiscale'],
    ['email', 'Email'],
    ['telefono', 'Telefono'],
    ['scadenza_certificazione', 'Scadenza certificazione'],
    ['consenso_privacy', 'Consenso privacy'],
  ];
  const SAVE_DELAY = 450;
  let checkoutResume = false;

  const clone = (value) => JSON.parse(JSON.stringify(value));

  class RsrlParticipants extends HTMLElement {
    constructor() {
      super();
      this.data = [];
      this.savedParticipantCount = 0;
      this.requiredCount = 1;
      this.dirty = false;
      this.saveTimer = null;
      this.savePromise = null;
    }

    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.list = this.querySelector('[data-rsrl-participant-list]');
      this.template = this.querySelector('[data-rsrl-participant-template]');
      this.addButton = this.querySelector('[data-rsrl-add-participant]');
      this.status = this.querySelector('[data-rsrl-participants-status]');

      try {
        const initial = JSON.parse(this.dataset.initial || '[]');
        this.data = Array.isArray(initial) ? initial.map((participant) => this.normalizeParticipant(participant)) : [];
      } catch (error) {
        this.data = [];
      }
      this.savedParticipantCount = Math.max(Number(this.dataset.initialCount) || 0, this.data.length);
      this.requiredCount = Math.max(Number(this.dataset.requiredCount) || 1, 1);
      while (this.data.length < this.requiredCount) this.data.push(this.emptyParticipant());

      this.addButton?.addEventListener('click', () => this.addParticipant());
      this.addEventListener('input', (event) => this.handleInput(event));
      this.addEventListener('change', (event) => this.handleInput(event));
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-rsrl-remove-participant]');
        if (!remove) return;
        const card = remove.closest('[data-rsrl-participant]');
        const index = Number(card?.dataset.index);
        if (Number.isInteger(index)) this.removeParticipant(index);
      });
      document.addEventListener('rsrl:participants-updated', (event) => {
        if (event.detail?.source === this || !Array.isArray(event.detail?.data)) return;
        this.data = clone(event.detail.data);
        this.savedParticipantCount = this.data.length;
        this.dirty = false;
        window.clearTimeout(this.saveTimer);
        this.render();
        this.setStatus(event.detail.message || 'Dati partecipanti salvati.');
      });

      this.render();
    }

    normalizeParticipant(participant = {}) {
      const previousName = String(participant.nome_cognome || '').trim().split(/\s+/);
      return {
        corso: participant.corso || '',
        nome: participant.nome || previousName.shift() || '',
        cognome: participant.cognome || previousName.join(' '),
        data_nascita: participant.data_nascita || '',
        luogo_nascita: participant.luogo_nascita || '',
        codice_fiscale: participant.codice_fiscale || '',
        email: participant.email || '',
        telefono: participant.telefono || '',
        scadenza_certificazione: participant.scadenza_certificazione || '',
        consenso_privacy: Boolean(participant.consenso_privacy),
      };
    }

    emptyParticipant() {
      return {
        corso: '',
        nome: '',
        cognome: '',
        data_nascita: '',
        luogo_nascita: '',
        codice_fiscale: '',
        email: '',
        telefono: '',
        scadenza_certificazione: '',
        consenso_privacy: false,
      };
    }

    addParticipant() {
      if (this.data.length && !this.isParticipantComplete(this.data.length - 1)) {
        this.reportIncomplete();
        return;
      }
      if (this.data.length >= this.requiredCount) return;
      this.data.push(this.emptyParticipant());
      this.dirty = true;
      this.render();
      const card = this.list?.lastElementChild;
      card?.querySelector('input:not([type="hidden"]), select, textarea')?.focus();
      this.scheduleSave();
    }

    removeParticipant(index) {
      if (this.data.length <= this.requiredCount) return;
      this.data.splice(index, 1);
      this.dirty = true;
      this.render();
      this.scheduleSave();
    }

    handleInput(event) {
      const field = event.target.closest('[data-rsrl-field]');
      const card = field?.closest('[data-rsrl-participant]');
      if (!field || !card) return;
      const index = Number(card.dataset.index);
      if (!this.data[index]) return;
      this.data[index][field.name] = field.type === 'checkbox' ? field.checked : field.value;
      if (field.name === 'corso') this.updateCertificationField(card, this.data[index]);
      this.dirty = true;
      this.updateAddButton();
      window.clearTimeout(this.saveTimer);
      if (this.allComplete()) this.scheduleSave();
    }

    render() {
      if (!this.list || !this.template) return;
      this.list.replaceChildren();
      this.data.forEach((participant, index) => {
        const fragment = this.template.content.cloneNode(true);
        const card = fragment.querySelector('[data-rsrl-participant]');
        const title = fragment.querySelector('[data-rsrl-participant-title]');
        const remove = fragment.querySelector('[data-rsrl-remove-participant]');
        card.dataset.index = String(index);
        title.textContent = `Partecipante ${index + 1}`;
        if (remove) remove.hidden = this.data.length <= this.requiredCount;
        fragment.querySelectorAll('[data-rsrl-field]').forEach((field) => {
          const value = participant[field.name];
          if (field.type === 'checkbox') field.checked = Boolean(value);
          else if (value !== undefined && value !== null) field.value = value;
        });
        this.updateCertificationField(card, participant);
        this.list.appendChild(fragment);
      });
      this.updateAddButton();
    }

    updateCertificationField(card, participant) {
      const course = card.querySelector('[name="corso"]');
      const wrapper = card.querySelector('[data-rsrl-certification-field]');
      const input = wrapper?.querySelector('[name="scadenza_certificazione"]');
      if (!course || !wrapper || !input) return;
      const selectedOption = course.tagName === 'SELECT' ? course.selectedOptions[0] : course;
      const required = selectedOption?.dataset.requiresCertification === 'true';
      wrapper.hidden = !required;
      input.disabled = !required;
      input.required = required;
      if (!required) {
        participant.scadenza_certificazione = '';
        input.value = '';
      }
    }

    updateAddButton() {
      if (!this.addButton) return;
      const next = this.data.length + 1;
      const canAdd = this.data.length < this.requiredCount && this.isParticipantComplete(this.data.length - 1);
      this.addButton.hidden = !canAdd;
      this.addButton.textContent = `Partecipante ${next} +`;
      this.addButton.setAttribute('aria-expanded', String(this.data.length > 0));
    }

    isParticipantComplete(index) {
      const card = this.list?.querySelector(`[data-index="${index}"]`);
      if (!card) return false;
      return Array.from(card.querySelectorAll('[required]')).every((field) => field.checkValidity());
    }

    allComplete() {
      return this.data.length >= this.requiredCount && this.data.every((participant, index) => this.isParticipantComplete(index));
    }

    reportIncomplete() {
      const invalid = this.list?.querySelector(':invalid');
      invalid?.reportValidity();
      invalid?.focus();
      this.setStatus('Completa tutti i campi obbligatori prima di proseguire.', true);
    }

    scheduleSave() {
      window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(() => this.save().catch(() => {}), SAVE_DELAY);
    }

    async flush() {
      window.clearTimeout(this.saveTimer);
      if (!this.allComplete()) {
        this.reportIncomplete();
        throw new Error('Dati partecipanti incompleti');
      }
      if (this.dirty) await this.save();
      else if (this.savePromise) await this.savePromise;
    }

    async save() {
      if (this.savePromise) return this.savePromise;
      if (!this.dirty) return;
      const snapshot = clone(this.data);
      const attributes = this.buildParticipantAttributes(snapshot);
      const clearedAttributes = Object.keys(attributes).reduce((cleared, attributeName) => {
        cleared[attributeName] = '';
        return cleared;
      }, {});
      this.setStatus('Salvataggio dei dati in corso…');
      this.toggleCheckout(true);
      const updateCartAttributes = (nextAttributes) =>
        fetch(this.dataset.updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: nextAttributes }),
        }).then((response) => {
          if (!response.ok) throw new Error(`Errore Shopify ${response.status}`);
          return response.json();
        });

      this.savePromise = updateCartAttributes(clearedAttributes)
        .then(() => updateCartAttributes(attributes))
        .then(() => {
          this.savedParticipantCount = snapshot.length;
          this.dirty = JSON.stringify(this.data) !== JSON.stringify(snapshot);
          this.setStatus('Dati partecipanti salvati.');
          document.dispatchEvent(
            new CustomEvent('rsrl:participants-updated', {
              detail: { source: this, data: snapshot, message: 'Dati partecipanti salvati.' },
            })
          );
        })
        .catch(() => {
          this.dirty = true;
          this.setStatus('Non è stato possibile salvare i dati. Riprova prima del checkout.', true);
          throw new Error('Salvataggio partecipanti non riuscito');
        })
        .finally(() => {
          this.savePromise = null;
          this.toggleCheckout(false);
          if (this.dirty && this.allComplete()) this.scheduleSave();
        });
      return this.savePromise;
    }

    buildParticipantAttributes(snapshot) {
      const attributes = {
        [ATTRIBUTE_NAME]: '',
        [PARTICIPANT_COUNT_ATTRIBUTE]: String(snapshot.length),
      };
      const maximumCount = Math.max(this.savedParticipantCount, snapshot.length);

      for (let index = 0; index < maximumCount; index += 1) {
        const participant = snapshot[index];
        PARTICIPANT_FIELDS.forEach(([field, label]) => {
          const attributeName = `Partecipante ${index + 1} - ${label}`;
          if (!participant) {
            attributes[attributeName] = '';
          } else if (field === 'consenso_privacy') {
            attributes[attributeName] = participant[field] ? 'Sì' : '';
          } else {
            attributes[attributeName] = String(participant[field] || '').trim();
          }
        });
      }

      return attributes;
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.error = String(error);
    }

    toggleCheckout(disabled) {
      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (button.closest('.is-empty')) return;
        button.disabled = disabled;
        button.setAttribute('aria-busy', String(disabled));
      });
    }
  }

  class RsrlCompanyBilling extends HTMLElement {
    constructor() {
      super();
      this.dirty = false;
      this.saveTimer = null;
      this.savePromise = null;
      this.handleSharedUpdate = (event) => {
        if (event.detail?.source === this || !event.detail?.values) return;
        this.inputs.forEach((input) => {
          if (Object.prototype.hasOwnProperty.call(event.detail.values, input.dataset.attribute)) {
            input.value = event.detail.values[input.dataset.attribute] || '';
          }
        });
        this.dirty = false;
        window.clearTimeout(this.saveTimer);
        this.setStatus(event.detail.message || 'Dati di fatturazione salvati.');
      };
    }

    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.inputs = Array.from(this.querySelectorAll('[data-rsrl-company-field]'));
      this.status = this.querySelector('[data-rsrl-company-status]');
      this.addEventListener('input', () => {
        this.dirty = true;
        this.scheduleSave();
      });
      document.addEventListener('rsrl:company-billing-updated', this.handleSharedUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('rsrl:company-billing-updated', this.handleSharedUpdate);
      window.clearTimeout(this.saveTimer);
    }

    values() {
      return this.inputs.reduce((attributes, input) => {
        attributes[input.dataset.attribute] = input.value.trim();
        return attributes;
      }, { 'Fatturazione azienda - Paese': '' });
    }

    scheduleSave() {
      window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(() => this.save().catch(() => {}), SAVE_DELAY);
    }

    async flush() {
      window.clearTimeout(this.saveTimer);
      if (this.dirty) await this.save();
      else if (this.savePromise) await this.savePromise;
    }

    async save() {
      if (this.savePromise) return this.savePromise;
      if (!this.dirty) return;
      const snapshot = this.values();
      this.setStatus('Salvataggio dei dati in corso…');
      this.toggleCheckout(true);
      this.savePromise = fetch(this.dataset.updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes: snapshot }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Errore Shopify ' + response.status);
          return response.json();
        })
        .then(() => {
          this.dirty = JSON.stringify(this.values()) !== JSON.stringify(snapshot);
          this.setStatus('Dati di fatturazione salvati.');
          document.dispatchEvent(
            new CustomEvent('rsrl:company-billing-updated', {
              detail: { source: this, values: snapshot, message: 'Dati di fatturazione salvati.' },
            })
          );
        })
        .catch(() => {
          this.dirty = true;
          this.setStatus('Non è stato possibile salvare i dati. Riprova prima del checkout.', true);
          throw new Error('Salvataggio fatturazione non riuscito');
        })
        .finally(() => {
          this.savePromise = null;
          this.toggleCheckout(false);
          if (this.dirty) this.scheduleSave();
        });
      return this.savePromise;
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.error = String(error);
    }

    toggleCheckout(disabled) {
      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (button.closest('.is-empty')) return;
        button.disabled = disabled;
        button.setAttribute('aria-busy', String(disabled));
      });
    }
  }

  if (!customElements.get('rsrl-participants')) customElements.define('rsrl-participants', RsrlParticipants);
  if (!customElements.get('rsrl-company-billing')) customElements.define('rsrl-company-billing', RsrlCompanyBilling);

  document.addEventListener(
    'click',
    async (event) => {
      const checkout = event.target.closest('button[name="checkout"]');
      if (!checkout || checkoutResume) return;
      const components = Array.from(document.querySelectorAll('rsrl-participants'));
      const active =
        components.find((component) => component.dirty) ||
        components.find((component) => component.data?.length);
      const companyComponents = Array.from(document.querySelectorAll('rsrl-company-billing'));
      const activeCompanyComponents = companyComponents.filter((component) => component.dirty || component.savePromise);
      if (!active) {
        event.preventDefault();
        event.stopPropagation();
        window.location.assign('/cart');
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      try {
        if (active) await active.flush();
        for (const component of activeCompanyComponents) await component.flush();
        checkoutResume = true;
        checkout.disabled = false;
        checkout.form?.requestSubmit(checkout);
      } catch (error) {
        checkout.disabled = false;
      } finally {
        window.setTimeout(() => {
          checkoutResume = false;
        }, 0);
      }
    },
    true
  );
})();
